import {
  CHANGE_RISK_LABELS,
  PROBLEM_LIFECYCLE_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  type ChangeRisk,
  type ProblemLifecycle,
  type TicketPriority,
  type TicketStatus,
  type TicketType,
} from '@gb-ticket/shared-types';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Chip fills. German labels stay; the role only adds a second cue and follows light, dark, and neutral. */
const statusTone = {
  info: 'border-status-info-border bg-status-info text-status-info-foreground',
  success: 'border-status-success-border bg-status-success text-status-success-foreground',
  warning: 'border-status-warning-border bg-status-warning text-status-warning-foreground',
  danger: 'border-status-danger-border bg-status-danger text-status-danger-foreground',
  neutral: 'border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
} as const;

function ticketStatusTone(status: TicketStatus): string {
  switch (status) {
    case 'NEW':
    case 'WAITING_FOR_CUSTOMER':
      return statusTone.info;
    case 'IN_PROGRESS':
    case 'WAITING_FOR_APPROVAL':
    case 'REOPENED':
      return statusTone.warning;
    case 'RESOLVED':
      return statusTone.success;
    case 'CLOSED':
      return statusTone.neutral;
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

export function StatusBadge({ status }: { status: TicketStatus }) {
  return (
    <Badge variant="outline" className={ticketStatusTone(status)}>
      {TICKET_STATUS_LABELS[status]}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: TicketPriority }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        priority === 'LOW' && statusTone.neutral,
        priority === 'MEDIUM' && statusTone.info,
        priority === 'HIGH' && statusTone.warning,
        priority === 'CRITICAL' && statusTone.danger,
      )}
    >
      {TICKET_PRIORITY_LABELS[priority]}
    </Badge>
  );
}

export function TypeBadge({ type }: { type: TicketType }) {
  return <Badge variant="secondary">{TICKET_TYPE_LABELS[type]}</Badge>;
}

export function MajorIncidentBadge() {
  return (
    <Badge variant="outline" className={statusTone.danger}>
      Major Incident
    </Badge>
  );
}

export function ProblemLifecycleBadge({ lifecycle }: { lifecycle: ProblemLifecycle }) {
  return (
    <Badge
      variant="outline"
      className={
        lifecycle === 'RESOLVED'
          ? statusTone.success
          : lifecycle === 'WORKAROUND_AVAILABLE'
            ? statusTone.warning
            : statusTone.info
      }
    >
      {PROBLEM_LIFECYCLE_LABELS[lifecycle]}
    </Badge>
  );
}

export function KnownErrorBadge() {
  return (
    <Badge variant="outline" className={statusTone.info}>
      Known Error
    </Badge>
  );
}

export function ChangeRiskBadge({ risk }: { risk: ChangeRisk }) {
  return (
    <Badge
      variant="outline"
      className={
        risk === 'HIGH'
          ? statusTone.danger
          : risk === 'MEDIUM'
            ? statusTone.warning
            : statusTone.success
      }
    >
      Risiko {CHANGE_RISK_LABELS[risk]}
    </Badge>
  );
}

export function ChangeApprovalBadge({
  status,
}: {
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'CANCELLED' | null | undefined;
}) {
  if (status === 'APPROVED') {
    return (
      <Badge variant="outline" className={statusTone.success}>
        Freigegeben
      </Badge>
    );
  }
  if (status === 'REJECTED') {
    return (
      <Badge variant="outline" className={statusTone.danger}>
        Abgelehnt
      </Badge>
    );
  }
  if (status === 'PENDING') {
    return (
      <Badge variant="outline" className={statusTone.warning}>
        Freigabe ausstehend
      </Badge>
    );
  }
  if (status === 'CANCELLED') {
    return (
      <Badge variant="outline" className={statusTone.neutral}>
        Freigabe abgebrochen
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={statusTone.neutral}>
      Keine Freigabe
    </Badge>
  );
}
